import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{
    error: Error | null;
    resetError: () => void;
  }>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Report error to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
    
    // Log specific DOM manipulation errors
    if (error.message?.includes('removeChild') || 
        error.message?.includes('appendChild') ||
        error.message?.includes('insertBefore')) {
      console.error('DOM manipulation error detected:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }

    // Log chunk loading errors
    if (this.isChunkLoadError(error)) {
      console.error('Chunk loading error detected:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  isChunkLoadError = (error: Error): boolean => {
    return (
      error.message.includes('Loading chunk') ||
      error.message.includes('dynamically imported module') ||
      error.message.includes('ChunkLoadError') ||
      error.message.includes('Loading CSS chunk') ||
      error.name === 'ChunkLoadError'
    );
  };

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback } = this.props;
      
      if (Fallback) {
        return <Fallback error={this.state.error} resetError={this.resetError} />;
      }

      const isChunkError = this.state.error && this.isChunkLoadError(this.state.error);

      return (
        <div className="flex flex-col items-center justify-center min-h-screen text-center px-4 bg-background text-foreground">
          <div className="text-6xl md:text-7xl mb-4">
            <AlertTriangle className="w-16 h-16 md:w-20 md:h-20 text-orange-500 mx-auto" />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
            {isChunkError ? 'Update Required' : 'Something went wrong!'}
          </h1>

          <p className="text-md md:text-lg text-muted-foreground mb-8 max-w-md">
            {isChunkError
              ? 'The app has been updated. Please refresh the page to get the latest version.'
              : 'An unexpected error occurred. Please try refreshing the page or return to the homepage.'}
          </p>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="text-left text-xs bg-muted p-3 rounded mb-6 max-w-md w-full">
              <summary className="font-medium cursor-pointer">
                Error Details (Development Only)
              </summary>
              <pre className="mt-2 text-red-600 dark:text-red-400 whitespace-pre-wrap">
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => window.location.reload()}
              size="lg"
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              <RefreshCw className="mr-2 h-5 w-5" />
              Refresh Page
            </Button>
            
            {!isChunkError && (
            <Button onClick={this.resetError} variant="outline" size="lg">
              Try Again
            </Button>
          )}
          
          <Button
              asChild
              variant="outline"
              size="lg"
            >
              <Link to="/">
                <Home className="mr-2 h-5 w-5" />
                Go Home
              </Link>
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
